package com.merkost.handpass.sharedweb.theme

import com.merkost.handpass.theme.HandpassTokens
import com.varabyte.kobweb.compose.css.CSSTimeNumericValue
import com.varabyte.kobweb.compose.css.StyleVariable
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.CSSLengthValue
import org.jetbrains.compose.web.css.CSSPercentageValue
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

object WebTokenHex {
    const val Ink = "#111111"
    const val White = "#FFFFFF"
    const val BrandActionOn = "#3A170B"

    object SiteLight {
        const val Background = White
        const val Surface = White
        const val SurfaceMuted = "#F8F8F6"
        const val OnSurface = Ink
        const val OnSurfaceMuted = "#666666"
        const val OnSurfaceFaint = "#666666"
        const val Border = "#E8E8E4"
        const val BorderStrong = "#C9C9C2"
        const val RowHover = "#F8F8F6"
        const val Overlay = "rgba(28,22,15,0.5)"
        const val SkeletonHighlight = White
        const val Accent = HandpassTokens.Colors.Primary
        const val AccentHover = HandpassTokens.Colors.PrimaryPressed
        const val AccentPressed = HandpassTokens.Colors.PrimaryPressed
        const val AccentText = HandpassTokens.Colors.PrimaryText
        const val AccentSoft = HandpassTokens.Colors.PrimarySoft
        const val AccentRing = "rgba(232,97,42,0.25)"
        const val OnAccent = Ink
        const val ActionAccent = HandpassTokens.Colors.Action
        const val ActionAccentHover = HandpassTokens.Colors.ActionHover
        const val ActionAccentPressed = HandpassTokens.Colors.ActionPressed
        const val OnActionAccent = HandpassTokens.Colors.ActionOn
        const val OnBrandActionAccent = BrandActionOn
        const val Success = HandpassTokens.Colors.SuccessGreen
        const val SuccessSoft = "#ECFAF4"
        const val Danger = HandpassTokens.Colors.ErrorRed
        const val DangerSoft = "#FDECEC"
        const val DangerHover = "#C42B2B"
        const val Warning = HandpassTokens.Colors.WarningYellow
        const val WarningSoft = "#FCF3E3"
        const val Info = HandpassTokens.Colors.InfoBlue
        const val InfoSoft = "#EFF6FF"
        const val RatingStar = "#B76B00"
    }

    object SiteDark {
        const val Background = "#151517"
        const val Surface = "#1E1E20"
        const val SurfaceMuted = "#232325"
        const val OnSurface = "#F2EDE8"
        const val OnSurfaceMuted = "#9E978F"
        const val OnSurfaceFaint = "#8B867E"
        const val Border = "#2A2926"
        const val BorderStrong = "#36342F"
        const val RowHover = "#26251F"
        const val Overlay = "rgba(0,0,0,0.7)"
        const val SkeletonHighlight = "#34332F"
        const val Accent = HandpassTokens.Colors.PrimaryDark
        const val AccentHover = "#FF895E"
        const val AccentPressed = HandpassTokens.Colors.Primary
        const val AccentText = HandpassTokens.Colors.PrimaryTextDark
        const val AccentSoft = "#2A1C14"
        const val AccentRing = "rgba(232,97,42,0.28)"
        const val OnAccent = Ink
        const val ActionAccent = HandpassTokens.Colors.Action
        const val ActionAccentHover = HandpassTokens.Colors.ActionHover
        const val ActionAccentPressed = HandpassTokens.Colors.ActionPressed
        const val OnActionAccent = HandpassTokens.Colors.ActionOn
        const val OnBrandActionAccent = BrandActionOn
        const val Success = "#34C285"
        const val SuccessSoft = "#15241C"
        const val Danger = "#F26060"
        const val DangerSoft = "#2A1717"
        const val DangerHover = "#FF7575"
        const val Warning = "#FBB13C"
        const val WarningSoft = "#2A2113"
        const val Info = HandpassTokens.Colors.InfoBlue
        const val InfoSoft = "#1A2536"
        const val RatingStar = "#FFB800"
    }
}

val Bg by StyleVariable<CSSColorValue>(Color(WebTokenHex.SiteLight.Background))
val Surface by StyleVariable<CSSColorValue>(Color(WebTokenHex.White))
val SurfaceMuted by StyleVariable<CSSColorValue>(Color(WebTokenHex.SiteLight.SurfaceMuted))
val OnSurface by StyleVariable<CSSColorValue>(Color(WebTokenHex.Ink))
val OnSurfaceMuted by StyleVariable<CSSColorValue>(Color(WebTokenHex.SiteLight.OnSurfaceMuted))
val OnSurfaceFaint by StyleVariable<CSSColorValue>(Color(WebTokenHex.SiteLight.OnSurfaceFaint))
val Border by StyleVariable<CSSColorValue>(Color(WebTokenHex.SiteLight.Border))
val BorderStrong by StyleVariable<CSSColorValue>(Color(WebTokenHex.SiteLight.BorderStrong))
val RowHover by StyleVariable<CSSColorValue>(Color(WebTokenHex.SiteLight.RowHover))
val Overlay by StyleVariable<CSSColorValue>(Color("rgba(28,22,15,0.5)"))
val OverlayLight by StyleVariable<CSSColorValue>(Color("rgba(0,0,0,0.04)"))
val OverlayHeavy by StyleVariable<CSSColorValue>(Color("rgba(0,0,0,0.7)"))
val SkeletonHighlight by StyleVariable<CSSColorValue>(Color("#FFFFFF"))
val Transparent by StyleVariable<CSSColorValue>(Color("transparent"))
val FixedInk by StyleVariable<CSSColorValue>(Color(WebTokenHex.Ink))
val FixedOnInk by StyleVariable<CSSColorValue>(Color(WebTokenHex.White))

val Accent by StyleVariable<CSSColorValue>(Color("#E8612A"))
val AccentHover by StyleVariable<CSSColorValue>(Color("#D4551F"))
val AccentPressed by StyleVariable<CSSColorValue>(Color("#C34B19"))
val AccentText by StyleVariable<CSSColorValue>(Color("#B64216"))
val AccentSoft by StyleVariable<CSSColorValue>(Color("#FFF0EB"))
val AccentRing by StyleVariable<CSSColorValue>(Color("rgba(232,97,42,0.25)"))
val OnAccent by StyleVariable<CSSColorValue>(Color(WebTokenHex.White))
val ActionAccent by StyleVariable<CSSColorValue>(Color("#C95024"))
val ActionAccentHover by StyleVariable<CSSColorValue>(Color("#B8441D"))
val ActionAccentPressed by StyleVariable<CSSColorValue>(Color("#A53C1A"))
val OnActionAccent by StyleVariable<CSSColorValue>(Color("#FFFFFF"))
val OnBrandActionAccent by StyleVariable<CSSColorValue>(Color(WebTokenHex.BrandActionOn))
val RatingStar by StyleVariable<CSSColorValue>(Color("#B76B00"))

val Success by StyleVariable<CSSColorValue>(Color("#1E7F55"))
val SuccessSoft by StyleVariable<CSSColorValue>(Color("#ECFAF4"))
val Danger by StyleVariable<CSSColorValue>(Color("#C93232"))
val DangerSoft by StyleVariable<CSSColorValue>(Color("#FDECEC"))
val DangerHover by StyleVariable<CSSColorValue>(Color("#B72424"))
val Warning by StyleVariable<CSSColorValue>(Color("#98600B"))
val WarningSoft by StyleVariable<CSSColorValue>(Color("#FCF3E3"))
val Info by StyleVariable<CSSColorValue>(Color("#2368B8"))
val InfoSoft by StyleVariable<CSSColorValue>(Color("#EFF6FF"))

val SpaceXxs by StyleVariable<CSSLengthValue>(2.px)
val SpaceXs by StyleVariable<CSSLengthValue>(4.px)
val SpaceSm by StyleVariable<CSSLengthValue>(8.px)
val SpaceMd by StyleVariable<CSSLengthValue>(12.px)
val SpaceDefault by StyleVariable<CSSLengthValue>(16.px)
val SpaceLg by StyleVariable<CSSLengthValue>(20.px)
val SpaceXl by StyleVariable<CSSLengthValue>(24.px)
val SpaceXxl by StyleVariable<CSSLengthValue>(36.px)
val SpaceXxxl by StyleVariable<CSSLengthValue>(48.px)

val RadiusXs by StyleVariable<CSSLengthValue>(4.px)
val RadiusSm by StyleVariable<CSSLengthValue>(8.px)
val RadiusMd by StyleVariable<CSSLengthValue>(12.px)
val RadiusLg by StyleVariable<CSSLengthValue>(20.px)
val RadiusCard by StyleVariable<CSSLengthValue>(16.px)
val RadiusThumb by StyleVariable<CSSLengthValue>(14.px)
val RadiusControl by StyleVariable<CSSLengthValue>(8.px)
val RadiusButton by StyleVariable<CSSLengthValue>(10.px)
val RadiusLarge by StyleVariable<CSSLengthValue>(20.px)
val RadiusPill by StyleVariable<CSSLengthValue>(999.px)
val RadiusFull by StyleVariable<CSSPercentageValue>(50.percent)

val Pad by StyleVariable<CSSLengthValue>(22.px)
val PadLg by StyleVariable<CSSLengthValue>(28.px)
val GapDefault by StyleVariable<CSSLengthValue>(18.px)
val DensityV by StyleVariable<CSSLengthValue>(16.px)
val DensityH by StyleVariable<CSSLengthValue>(18.px)
val TouchTarget by StyleVariable<CSSLengthValue>(44.px)
val ThumbSize by StyleVariable<CSSLengthValue>(40.px)
val ButtonGap by StyleVariable<CSSLengthValue>(7.px)
val ButtonSmPadV by StyleVariable<CSSLengthValue>(6.px)
val ButtonSmPadH by StyleVariable<CSSLengthValue>(11.px)
val ButtonSmType by StyleVariable<CSSLengthValue>(12.5.px)
val ButtonSmIcon by StyleVariable<CSSLengthValue>(16.px)
val ButtonSmSpinner by StyleVariable<CSSLengthValue>(12.px)
val ButtonMdPadV by StyleVariable<CSSLengthValue>(9.px)
val ButtonMdPadH by StyleVariable<CSSLengthValue>(16.px)
val ButtonMdType by StyleVariable<CSSLengthValue>(13.px)
val ButtonMdIcon by StyleVariable<CSSLengthValue>(18.px)
val ButtonMdSpinner by StyleVariable<CSSLengthValue>(14.px)
val ButtonLgPadV by StyleVariable<CSSLengthValue>(12.px)
val ButtonLgPadH by StyleVariable<CSSLengthValue>(18.px)
val ButtonLgType by StyleVariable<CSSLengthValue>(15.px)
val ButtonLgIcon by StyleVariable<CSSLengthValue>(20.px)
val ButtonLgSpinner by StyleVariable<CSSLengthValue>(16.px)

val TypeBase by StyleVariable<CSSLengthValue>(14.5.px)
val TypeXs by StyleVariable<CSSLengthValue>(11.px)
val TypeSm by StyleVariable<CSSLengthValue>(12.px)
val TypeCaption by StyleVariable<CSSLengthValue>(13.px)
val TypeBody by StyleVariable<CSSLengthValue>(14.px)
val TypeMd by StyleVariable<CSSLengthValue>(16.px)
val TypeLg by StyleVariable<CSSLengthValue>(18.px)
val TypeTitle by StyleVariable<CSSLengthValue>(22.px)
val TypeXl by StyleVariable<CSSLengthValue>(26.px)
val TypeStat by StyleVariable<CSSLengthValue>(30.px)
val TypeXxl by StyleVariable<CSSLengthValue>(32.px)
val TypeHero by StyleVariable<CSSLengthValue>(44.px)
val TypeDenseTitle by StyleVariable<CSSLengthValue>(15.px)
val TypeDenseBody by StyleVariable<CSSLengthValue>(13.5.px)
val TypeDenseLabel by StyleVariable<CSSLengthValue>(12.5.px)
val TypeBadge by StyleVariable<CSSLengthValue>(12.px)
val TypePill by StyleVariable<CSSLengthValue>(11.5.px)
val TypeMicro by StyleVariable<CSSLengthValue>(11.px)
val TypeNav by StyleVariable<CSSLengthValue>(10.5.px)

val TrackingWide by StyleVariable<String>("0.06em")
val TrackingBrand by StyleVariable<String>("-0.03em")
val TrackingHeading by StyleVariable<String>("-0.02em")

val FontSans by StyleVariable<String>("'${HandpassTokens.Fonts.Primary}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif")
val FontDisplay by StyleVariable<String>("'${HandpassTokens.Fonts.Display}', Georgia, serif")
val FontMono by StyleVariable<String>("'${HandpassTokens.Fonts.Mono}', 'SF Mono', 'Fira Code', monospace")

val ShadowCard by StyleVariable<String>("var(--hp-shadow-card)")
val ShadowCardHover by StyleVariable<String>("var(--hp-shadow-card-hover)")
val ShadowDropdown by StyleVariable<String>("var(--hp-shadow-dropdown)")
val ShadowModal by StyleVariable<String>("var(--hp-shadow-modal)")
val ShadowPop by StyleVariable<String>("var(--hp-shadow-pop)")
val ImageOutline by StyleVariable<String>("var(--hp-img-outline, rgba(0,0,0,0.1))")

val MotionReduced by StyleVariable<CSSTimeNumericValue>(0.01.ms)
val MotionPress by StyleVariable<CSSTimeNumericValue>(100.ms)
val MotionFast by StyleVariable<CSSTimeNumericValue>(130.ms)
val MotionNormal by StyleVariable<CSSTimeNumericValue>(200.ms)
val MotionSlow by StyleVariable<CSSTimeNumericValue>(320.ms)
val MotionFavoritePop by StyleVariable<CSSTimeNumericValue>(260.ms)
val MotionSpin by StyleVariable<CSSTimeNumericValue>(700.ms)
val MotionDraw by StyleVariable<CSSTimeNumericValue>(850.ms)
val MotionShimmer by StyleVariable<CSSTimeNumericValue>(1400.ms)
val MotionCelebration by StyleVariable<CSSTimeNumericValue>(1600.ms)
val MotionLongDelay by StyleVariable<CSSTimeNumericValue>(1050.ms)
val MotionReveal by StyleVariable<CSSTimeNumericValue>(240.ms)
val MotionRevealShift by StyleVariable<CSSLengthValue>(6.px)
val MotionEaseStandard by StyleVariable<AnimationTimingFunction>(
    AnimationTimingFunction.cubicBezier(0.4, 0.0, 0.2, 1.0)
)
val MotionEaseOut by StyleVariable<AnimationTimingFunction>(
    AnimationTimingFunction.cubicBezier(0.22, 0.61, 0.36, 1.0)
)
val MotionEaseLinear by StyleVariable<AnimationTimingFunction>(AnimationTimingFunction.Linear)
val MotionPressScale by StyleVariable<Number>(0.96)
val MotionModalScale by StyleVariable<Number>(0.97)
val MotionControlFocusScale by StyleVariable<Number>(1.12)
val MotionRevealScale by StyleVariable<Number>(0.985)
val MotionSubtleHoverScale by StyleVariable<Number>(1.01)
val MotionEditorialHoverScale by StyleVariable<Number>(1.015)
val MotionChartHoverScale by StyleVariable<Number>(1.02)
val MotionChartEntryInitialScale by StyleVariable<Number>(0.18)
val MotionAccentBarScale by StyleVariable<Number>(0.25)
val MotionImageHoverScale by StyleVariable<Number>(1.025)
val MotionFavoritePopScale by StyleVariable<Number>(1.18)
val MotionShareInitialScale by StyleVariable<Number>(0.25)
val MotionFeedbackInitialScale by StyleVariable<Number>(0.92)
val MotionFeedbackOvershootScale by StyleVariable<Number>(1.02)
val MotionFeedbackPulseScale by StyleVariable<Number>(1.3)
val MotionSoldBadgeInitialScale by StyleVariable<Number>(0.7)
val MotionSoldBadgeInitialTilt by StyleVariable<Number>(-4)
val MotionSoldBadgeActiveTilt by StyleVariable<Number>(-2)

val LayoutMaxWidth by StyleVariable<CSSLengthValue>(1280.px)
val LayoutMaxWidthWide by StyleVariable<CSSLengthValue>(1440.px)
val LayoutHeaderHeight by StyleVariable<CSSLengthValue>(64.px)
val LayoutHeaderRowMobile by StyleVariable<CSSLengthValue>(56.px)
val LayoutHeaderSearchRowMobile by StyleVariable<CSSLengthValue>(52.px)
val LayoutReadingWidth by StyleVariable<CSSLengthValue>(680.px)
val LayoutModalWidth by StyleVariable<CSSLengthValue>(640.px)
val LayoutDrawerWidth by StyleVariable<CSSLengthValue>(280.px)

val ZHeaderUnderlay by StyleVariable<Number>(39)
val ZHeader by StyleVariable<Number>(40)
val ZSticky by StyleVariable<Number>(60)
val ZPopover by StyleVariable<Number>(80)
val ZModal by StyleVariable<Number>(100)
val ZDrawerBackdrop by StyleVariable<Number>(200)
val ZDrawer by StyleVariable<Number>(201)
val ZToast by StyleVariable<Number>(300)
val ZSkipLink by StyleVariable<Number>(400)

object WebZ {
    val HeaderUnderlay get() = ZHeaderUnderlay.value().toString()
    val Header get() = ZHeader.value().toString()
    val Sticky get() = ZSticky.value().toString()
    val Popover get() = ZPopover.value().toString()
    val Modal get() = ZModal.value().toString()
    val DrawerBackdrop get() = ZDrawerBackdrop.value().toString()
    val Drawer get() = ZDrawer.value().toString()
    val Toast get() = ZToast.value().toString()
    val SkipLink get() = ZSkipLink.value().toString()
}

object WebFont {
    val Family get() = FontSans.value()
    val Display get() = FontDisplay.value()
    val Mono get() = FontMono.value()
}

object WebType {
    val Base get() = TypeBase.value()
    val Stat get() = TypeStat.value()
    val H1 get() = TypeXl.value()
    val Title get() = TypeDenseTitle.value()
    val Body get() = TypeDenseBody.value()
    val Label get() = TypeDenseLabel.value()
    val Badge get() = TypeBadge.value()
    val Pill get() = TypePill.value()
    val Micro get() = TypeMicro.value()
    val Nav get() = TypeNav.value()
}

object WebSize {
    val N0_5 by StyleVariable((-0.5).px)
    val N1 by StyleVariable((-1).px)
    val N2 by StyleVariable((-2).px)
    val N3 by StyleVariable((-3).px)
    val N4 by StyleVariable((-4).px)
    val N5 by StyleVariable((-5).px)
    val N6 by StyleVariable((-6).px)
    val N8 by StyleVariable((-8).px)
    val N9 by StyleVariable((-9).px)
    val N10 by StyleVariable((-10).px)
    val N12 by StyleVariable((-12).px)
    val N16 by StyleVariable((-16).px)
    val N26 by StyleVariable((-26).px)
    val N34 by StyleVariable((-34).px)
    val N38 by StyleVariable((-38).px)
    val N32 by StyleVariable((-32).px)
    val N42 by StyleVariable((-42).px)
    val N9999 by StyleVariable((-9999).px)
    val P0 by StyleVariable(0.px)
    val P0_5 by StyleVariable(0.5.px)
    val P1 by StyleVariable(1.px)
    val P1_5 by StyleVariable(1.5.px)
    val P2 by StyleVariable(2.px)
    val P3 by StyleVariable(3.px)
    val P4 by StyleVariable(4.px)
    val P5 by StyleVariable(5.px)
    val P6 by StyleVariable(6.px)
    val P7 by StyleVariable(7.px)
    val P8 by StyleVariable(8.px)
    val P9 by StyleVariable(9.px)
    val P9_5 by StyleVariable(9.5.px)
    val P10 by StyleVariable(10.px)
    val P10_5 by StyleVariable(10.5.px)
    val P11 by StyleVariable(11.px)
    val P11_5 by StyleVariable(11.5.px)
    val P12 by StyleVariable(12.px)
    val P12_5 by StyleVariable(12.5.px)
    val P13 by StyleVariable(13.px)
    val P13_5 by StyleVariable(13.5.px)
    val P14 by StyleVariable(14.px)
    val P14_5 by StyleVariable(14.5.px)
    val P15 by StyleVariable(15.px)
    val P15_5 by StyleVariable(15.5.px)
    val P16 by StyleVariable(16.px)
    val P17 by StyleVariable(17.px)
    val P18 by StyleVariable(18.px)
    val P18_5 by StyleVariable(18.5.px)
    val P19 by StyleVariable(19.px)
    val P20 by StyleVariable(20.px)
    val P21 by StyleVariable(21.px)
    val P22 by StyleVariable(22.px)
    val P24 by StyleVariable(24.px)
    val P26 by StyleVariable(26.px)
    val P28 by StyleVariable(28.px)
    val P30 by StyleVariable(30.px)
    val P32 by StyleVariable(32.px)
    val P34 by StyleVariable(34.px)
    val P36 by StyleVariable(36.px)
    val P38 by StyleVariable(38.px)
    val P40 by StyleVariable(40.px)
    val P42 by StyleVariable(42.px)
    val P44 by StyleVariable(44.px)
    val P46 by StyleVariable(46.px)
    val P48 by StyleVariable(48.px)
    val P50 by StyleVariable(50.px)
    val P52 by StyleVariable(52.px)
    val P56 by StyleVariable(56.px)
    val P60 by StyleVariable(60.px)
    val P64 by StyleVariable(64.px)
    val P66 by StyleVariable(66.px)
    val P72 by StyleVariable(72.px)
    val P73 by StyleVariable(73.px)
    val P78 by StyleVariable(78.px)
    val P80 by StyleVariable(80.px)
    val P84 by StyleVariable(84.px)
    val P88 by StyleVariable(88.px)
    val P92 by StyleVariable(92.px)
    val P96 by StyleVariable(96.px)
    val P100 by StyleVariable(100.px)
    val P120 by StyleVariable(120.px)
    val P132 by StyleVariable(132.px)
    val P150 by StyleVariable(150.px)
    val P160 by StyleVariable(160.px)
    val P168 by StyleVariable(168.px)
    val P170 by StyleVariable(170.px)
    val P180 by StyleVariable(180.px)
    val P190 by StyleVariable(190.px)
    val P200 by StyleVariable(200.px)
    val P210 by StyleVariable(210.px)
    val P220 by StyleVariable(220.px)
    val P232 by StyleVariable(232.px)
    val P240 by StyleVariable(240.px)
    val P260 by StyleVariable(260.px)
    val P262 by StyleVariable(262.px)
    val P280 by StyleVariable(280.px)
    val P286 by StyleVariable(286.px)
    val P300 by StyleVariable(300.px)
    val P320 by StyleVariable(320.px)
    val P340 by StyleVariable(340.px)
    val P350 by StyleVariable(350.px)
    val P360 by StyleVariable(360.px)
    val P400 by StyleVariable(400.px)
    val P404 by StyleVariable(404.px)
    val P420 by StyleVariable(420.px)
    val P440 by StyleVariable(440.px)
    val P470 by StyleVariable(470.px)
    val P480 by StyleVariable(480.px)
    val P500 by StyleVariable(500.px)
    val P520 by StyleVariable(520.px)
    val P540 by StyleVariable(540.px)
    val P560 by StyleVariable(560.px)
    val P580 by StyleVariable(580.px)
    val P600 by StyleVariable(600.px)
    val P624 by StyleVariable(624.px)
    val P640 by StyleVariable(640.px)
    val P680 by StyleVariable(680.px)
    val P700 by StyleVariable(700.px)
    val P720 by StyleVariable(720.px)
    val P860 by StyleVariable(860.px)
    val P980 by StyleVariable(980.px)
    val P999 by StyleVariable(999.px)
    val P1024 by StyleVariable(1024.px)
    val P1240 by StyleVariable(1240.px)
    val P1280 by StyleVariable(1280.px)
    val P1440 by StyleVariable(1440.px)
    val P1600 by StyleVariable(1600.px)
    val P9999 by StyleVariable(9999.px)
}
